import { useState, useCallback } from "react";
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from "../hooks/useApi";
import Card from "../components/Card";
import Button from "../components/Button";
import GlassModal from "../components/GlassModal";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import CopyButton from "../components/CopyButton";
import KeyDetailDrawer from "../components/KeyDetailDrawer";
import { formatTomanInt, parsePersianNumber, toPersianDigits, normalizeDigits } from "../utils/format";

/** Format a date as a relative Persian time string (e.g., "۲ ساعت پیش", "دیروز") */
function relativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "همین الان";
  if (diffMin < 60) return `${diffMin.toLocaleString("fa-IR")} دقیقه پیش`;
  if (diffHour < 24) return `${diffHour.toLocaleString("fa-IR")} ساعت پیش`;
  if (diffDay < 2) return "دیروز";
  if (diffDay < 7) return `${diffDay.toLocaleString("fa-IR")} روز پیش`;
  return new Date(dateStr).toLocaleDateString("fa-IR");
}

export default function ApiKeys() {
  const { data: keys, isLoading } = useApiKeys();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [enableLimit, setEnableLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdKeyName, setCreatedKeyName] = useState<string | null>(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState<string | null>(null);
  const [revokeConfirmText, setRevokeConfirmText] = useState("");
  const [selectedKey, setSelectedKey] = useState<Record<string, unknown> | null>(null);

  const keyToRevoke = showRevokeConfirm
    ? (keys || []).find((k: Record<string, unknown>) => k.id === showRevokeConfirm)
    : null;
  const revokeKeyName = (keyToRevoke?.name as string) || "بدون نام";
  const canRevoke = revokeConfirmText.trim() === revokeKeyName;

  const handleCreate = useCallback(() => {
    const name = newKeyName.trim() || "بدون نام";
    const displayName = newKeyName.trim() || "بدون نام";
    const limitToman = parsePersianNumber(limitInput);
    const creditLimit =
      enableLimit && limitToman > 0 ? Math.round(limitToman * 10) : null;
    createKey.mutate(
      { name, credit_limit: creditLimit },
      {
        onSuccess: (data) => {
          if (data && data.full_key) {
            setCreatedKey(data.full_key);
            setCreatedKeyName(displayName);
          }
          setNewKeyName("");
          setLimitInput("");
          setEnableLimit(false);
        },
      }
    );
  }, [newKeyName, enableLimit, limitInput, createKey]);

  const handleRevoke = useCallback((id: string) => {
    revokeKey.mutate(id, {
      onSuccess: () => setShowRevokeConfirm(null),
    });
  }, [revokeKey]);

  const closeRevokeModal = useCallback(() => {
    setShowRevokeConfirm(null);
    setRevokeConfirmText("");
  }, []);

  const handleCloseCreate = useCallback(() => {
    setShowCreate(false);
    setCreatedKey(null);
    setCreatedKeyName(null);
  }, []);

  const columns = [
    {
      key: "key_prefix",
      header: "کلید API",
      render: (item: Record<string, unknown>) => (
        <span className="font-mono text-small ltr text-left block text-text-main tracking-tight">{item.key_prefix as string}</span>
      ),
    },
    {
      key: "name",
      header: "نام",
      render: (item: Record<string, unknown>) => (
        <span className="text-text-main">{item.name ? String(item.name) : <span className="text-text-tertiary">بدون نام</span>}</span>
      ),
    },
    {
      key: "status",
      header: "وضعیت",
      render: (item: Record<string, unknown>) => (
        item.status === "active"
          ? <Badge variant="success">فعال</Badge>
          : <Badge variant="error">لغو شده</Badge>
      ),
    },
    {
      key: "credit_limit",
      header: "سقف هزینه",
      render: (item: Record<string, unknown>) => {
        const limit = Number(item.credit_limit);
        const spent = Number(item.spent) || 0;
        if (!limit || limit <= 0) {
          return <span className="text-small text-text-tertiary">نامحدود</span>;
        }
        const pct = Math.min(100, Math.round((spent / limit) * 100));
        return (
          <div className="flex flex-col gap-1 min-w-[130px]">
            <div className="flex items-center justify-between text-small">
              <span className="font-number text-text-main">{formatTomanInt(spent)}</span>
              <span className="text-text-tertiary">از {formatTomanInt(limit)} تومان</span>
            </div>
            <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: pct >= 100 ? "rgb(var(--status-error-text))" : "rgb(var(--color-primary))",
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: "last_used_at",
      header: "آخرین استفاده",
      render: (item: Record<string, unknown>) => {
        if (!item.last_used_at) return <span className="text-text-tertiary text-small">هنوز استفاده نشده</span>;
        return (
          <span className="text-small text-text-tertiary" title={new Date(item.last_used_at as string).toLocaleDateString("fa-IR", { hour: "2-digit", minute: "2-digit" })}>
            {relativeTime(item.last_used_at as string)}
          </span>
        );
      },
    },
    {
      key: "created_at",
      header: "تاریخ ساخت",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-tertiary">{new Date(item.created_at as string).toLocaleDateString("fa-IR")}</span>
      ),
    },
    {
      key: "actions",
      header: "عملیات",
      render: (item: Record<string, unknown>) =>
        item.status === "active" ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRevokeConfirmText("");
              setShowRevokeConfirm(item.id as string);
            }}
            className="text-small text-status-error-text hover:underline hover:text-red-600 transition-colors"
          >
            لغو
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 text-text-main">کلیدهای API</h1>
          <p className="text-body text-text-secondary mt-1">مدیریت کلیدهای دسترسی API</p>
        </div>
        <Button onClick={() => setShowCreate(true)} size="lg">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          ساخت کلید جدید
        </Button>
      </div>

      {/* Keys Table */}
      <Card padding="md">
        <DataTable
          columns={columns}
          data={(keys || []) as Record<string, unknown>[]}
          loading={isLoading}
          emptyMessage="هنوز هیچ کلید API ای ساخته نشده است. اولین کلید خود را بسازید."
          onRowClick={(item) => setSelectedKey(item)}
        />
      </Card>

      {/* Quick Start */}
      <Card padding="md">
        <h2 className="text-h2 text-text-main mb-4">راهنمای استفاده</h2>
        <div dir="ltr" className="api-guide-code bg-surface-muted rounded-btn p-4 border border-line overflow-x-auto">
          <pre className="text-mono text-text-main ltr text-left" dir="ltr">
            <code>{`from openai import OpenAI

client = OpenAI(
    api_key="YOUR_API_KEY",
    base_url="https://app.asha-ai.ir/v1"
)

response = client.chat.completions.create(
    model="openai/gpt-5.6-sol",
    messages=[{"role": "user", "content": "Hello!"}]
)`}</code>
          </pre>
        </div>
      </Card>

      {/* Create Key Modal */}
      <GlassModal
        open={showCreate}
        onClose={handleCloseCreate}
        title={createdKey ? "کلید API ساخته شد" : "ساخت کلید API جدید"}
      >
        {createdKey ? (
          <div className="space-y-5">
            {/* Warning banner */}
            <div className="bg-status-warning-bg rounded-btn p-3.5 border border-status-warning-text/10">
              <div className="flex items-start gap-2.5">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-status-warning-text shrink-0 mt-0.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <p className="text-small font-medium text-status-warning-text">این کلید فقط یک بار نمایش داده می‌شود</p>
                  <p className="text-small text-status-warning-text/70 mt-0.5">
                    آن را در جایی امن ذخیره کنید. پس از بستن این پنجره، دیگر قادر به مشاهده کل کامل نخواهید بود.
                  </p>
                </div>
              </div>
            </div>

            {/* Key name display */}
            {createdKeyName && createdKeyName !== "بدون نام" && (
              <p className="text-small text-text-secondary">
                نام کلید: <span className="font-medium text-text-main">{createdKeyName}</span>
              </p>
            )}

            {/* Key display with copy */}
            <div className="bg-surface-hover rounded-btn p-1">
              <div className="flex items-center gap-2 bg-surface rounded-[10px] px-4 py-3.5 border border-line">
                <code className="flex-1 font-mono text-small text-text-main ltr text-left break-all select-all tracking-tight leading-relaxed">
                  {createdKey}
                </code>
                <CopyButton text={createdKey} label="کپی" />
              </div>
            </div>

            {/* Python usage snippet */}
            <div className="bg-surface-muted rounded-btn p-3.5 border border-line">
              <p className="text-small text-text-tertiary mb-2">استفاده در کد:</p>
              <pre className="text-mono text-small text-text-main ltr text-left overflow-x-auto" dir="ltr">
                <code>{`from openai import OpenAI

client = OpenAI(
    api_key="${createdKey.slice(0, 19)}...",
    base_url="https://app.asha-ai.ir/v1"
)`}</code>
              </pre>
            </div>

            <Button className="w-full" onClick={handleCloseCreate}>
              متوجه شدم
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-small font-medium text-text-secondary mb-1.5">
                نام کلید
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
                className="w-full h-10 px-3.5 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder="مثال: کلید توسعه"
                autoFocus
              />
              <p className="text-small text-text-tertiary mt-1.5">
                برای شناسایی کلیدهای خود یک نام انتخاب کنید.
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2.5 cursor-pointer text-body text-text-main">
                <input
                  type="checkbox"
                  checked={enableLimit}
                  onChange={(e) => setEnableLimit(e.target.checked)}
                  className="w-4 h-4 rounded border-line accent-[var(--primary)]"
                />
                تعیین سقف هزینه برای این کلید
              </label>
              {enableLimit && (
                <div className="mt-3">
                  <label className="block text-small font-medium text-text-secondary mb-1.5">
                    سقف هزینه (تومان)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    dir="ltr"
                    value={toPersianDigits(limitInput)}
                    onChange={(e) => setLimitInput(normalizeDigits(e.target.value))}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
                    className="w-full h-10 px-3.5 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    placeholder="مثلاً ۵۰۰۰۰۰"
                    autoFocus
                  />
                  <p className="text-small text-text-tertiary mt-1.5">
                    پس از رسیدن هزینه‌ی کلید به این سقف، درخواست‌های جدید با این کلید
                    رد می‌شوند تا سقف را افزایش دهید یا آن را بردارید. برای نامحدود بودن،
                    این گزینه را خاموش بگذارید.
                  </p>
                </div>
              )}
            </div>
            <Button className="w-full" onClick={handleCreate} loading={createKey.isPending} disabled={!newKeyName.trim()}>
              ساخت کلید
            </Button>
          </div>
        )}
      </GlassModal>

      {/* Revoke Confirmation Modal */}
      <GlassModal
        open={!!showRevokeConfirm}
        onClose={closeRevokeModal}
        title="لغو کلید API"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 bg-status-error-bg rounded-btn p-3.5 border border-status-error-text/10">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-status-error-text shrink-0 mt-0.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div>
              <p className="text-small font-medium text-status-error-text">آیا از لغو این کلید اطمینان دارید؟</p>
              <p className="text-small text-status-error-text/70 mt-0.5">
                پس از لغو، دیگر نمی‌توانید از این کلید برای ارتباط با API استفاده کنید و این عملیات قابل بازگشت نیست.
                برای تأیید، نام کلید <span className="font-medium text-status-error-text">{revokeKeyName}</span> را در کادر زیر وارد کنید.
              </p>
            </div>
          </div>
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">
              نام کلید
            </label>
            <input
              type="text"
              value={revokeConfirmText}
              onChange={(e) => setRevokeConfirmText(e.target.value)}
              className="w-full h-10 px-3.5 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              placeholder={revokeKeyName}
              autoFocus
            />
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={closeRevokeModal}>
              انصراف
            </Button>
            <Button
              className="flex-1 !bg-status-error-text hover:!bg-red-700"
              onClick={() => handleRevoke(showRevokeConfirm!)}
              loading={revokeKey.isPending}
              disabled={!canRevoke}
            >
              لغو کلید
            </Button>
          </div>
        </div>
      </GlassModal>

      {/* Key Detail Drawer */}
      <KeyDetailDrawer selectedKey={selectedKey} onClose={() => setSelectedKey(null)} />
    </div>
  );
}